import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { LockFilled } from '@ant-design/icons';
import { menuItens } from './MenuItens';
import { getUserType, getUserPermissions } from '../../utils/users';
import './SidebarComponent.scss';
import logo from '../../assets/images/new-campo-digital.png';
import logoCollapsed from '../../assets/images/ic_launcher.png';
import { VERSION, BUILD_NUMBER } from '../../utils/constants';
import { toggleMenuCollapsed, changeHeaderTitle } from '../../redux/actions/app';

const SidebarComponent = (): React.ReactElement => {
  const navigate = useNavigate();
  const locationObject = useLocation();

  // URL path
  const [pathName, setPathName] = useState<string>('');
  const [userType] = useState<string | null>(getUserType());

  const dispatch = useDispatch();
  const { collapsed, policyAndCookiesUrl, lgpdUrl } = useSelector((state:any) => state.app);

  const toggleMenu = (): void => {
    dispatch(toggleMenuCollapsed());
  };

  const changeTitle = (headerTitle: string): void => {
    dispatch(changeHeaderTitle(headerTitle));
  };

  const getPolicyAndCookiesUrl = (): string | null => {
    if (localStorage.getItem('policyAndCookiesUrl') && localStorage.getItem('policyAndCookiesUrl') !== '') {
      return localStorage.getItem('policyAndCookiesUrl');
    }
    if (policyAndCookiesUrl) {
      return policyAndCookiesUrl;
    }
    return null;
  };

  const getLgpdUrl = (): string | null => {
    if (localStorage.getItem('lgpdUrl') && localStorage.getItem('lgpdUrl') !== '') {
      return localStorage.getItem('lgpdUrl');
    }
    if (lgpdUrl) {
      return lgpdUrl;
    }
    return null;
  };

  const menu = useMemo(() => [
    ...menuItens,
    {
      name: 'Privacidade',
      visibility: ['COOPERCITRUS', 'COOPERATIVE'],
      icon: <LockFilled />,
      permissions: ['Política de privacidade e cookies', 'LGPD'],
      subMenu: [
        {
          id: 1,
          name: 'Política de privacidade      e cookies',
          externalUrl: getPolicyAndCookiesUrl() || 'https://coopercitrus.com.br/politica-de-privacidade',
          visibility: ['COOPERCITRUS', 'COOPERATIVE'],
          permissions: ['Política de privacidade e cookies'],
          headerTitle: '',
          documentTitle: 'Privacidade',
        },
        {
          id: 2,
          name: 'LGPD',
          externalUrl: getLgpdUrl() || 'https://coopercitrus.com.br/lgpd/',
          visibility: ['COOPERCITRUS', 'COOPERATIVE'],
          permissions: ['LGPD'],
          headerTitle: '',
          documentTitle: 'Privacidade',
        },
      ],
      documentTitle: 'Privacidade',
    }], [getLgpdUrl, getPolicyAndCookiesUrl]);

  const getPageTitle = (): any => {
    // Return the first page title that contains the current browser URL path name
    for (let index = 0; index < menu.length; index += 1) {
      if (
        menu[index]?.standalone
        && locationObject.pathname.startsWith(menu[index]?.url)
      ) {
        return menu[index];
      }

      const found = menu[index]?.subMenu.find(
        (item) => locationObject.pathname.startsWith(item.url),
      );
      if (found !== undefined) {
        return found;
      }
    }

    return { documentTitle: '', headerTitle: '' };
  };

  const getOpenKeys = (): any => (
    // Return open menu keys for a URL path name
    menu.map((value, index) => {
      const foundUrl = value.subMenu.reduce(
        (previous, element) => previous
          || (locationObject.pathname.startsWith(element.url)), false,
      );

      return foundUrl ? `menu${index}` : '';
    })
  );

  const changePage = (url, headerTitle, toggleMenuP): void => {
    if (window.innerWidth <= 425) {
      toggleMenuP();
    }

    navigate(url);
    changeTitle(headerTitle);
  };

  const renderMenuItem = (item): React.ReactElement => {
    return ({
      key: item.url || item.id,

      onClick: (): void => {
        if (item.id) {
          window.open(item.externalUrl, item.externalTarget || '_blank');
        } else {
          changePage(item.url, item.headerTitle, toggleMenu);
        }
      },

      label: (
        <div className="submenu-container-text">
          <span className="menu-icon">{item.icon}</span>
          <span className={collapsed ? 'ivisible item-text' : 'item-text'}>
            {item.name}
          </span>
        </div>
      ),
    });
  };

  const renderSubMenu = (item, index): React.ReactElement => {
    const userPermissionsLocal = getUserPermissions();

    return ({
      key: `menu${index}`,
      className: 'submenu-container',

      label: (
        <div className="submenu-container-text">
          <span className="menu-icon">{item.icon}</span>
          <span className={collapsed ? 'ivisible item-text' : 'item-text'}>
            {item.name}
          </span>
        </div>
      ),

      children: [
        ...(item.subMenu.map(
          (subItem) => {
            if (!subItem.visibility.includes(userType)) {
              return null;
            }

            if ('checkPermissionExpressionMethod' in subItem && subItem['checkPermissionExpressionMethod'] != null) {
              if (!subItem['checkPermissionExpressionMethod']()) {
                return null;
              }

              return renderMenuItem(subItem);
            }

            if ('hasEveryPermission' in subItem && subItem.hasEveryPermission != null) {
              if (
                !(subItem.hasEveryPermission || []).every(
                  (element) => (userPermissionsLocal || []).includes(element),
                )
              ) {
                return null;
              }

              return renderMenuItem(subItem);
            }

            if (
              !(subItem.permissions || []).some(
                (element) => (userPermissionsLocal || []).includes(element),
              )
            ) {
              return null;
            }

            return renderMenuItem(subItem);
          },
        )),
      ],
    });
  };

  useEffect(() => {
    // update selector when the page is refreshed
    if (pathName !== locationObject.pathname) {
      // Refresh URL path name
      setPathName(locationObject.pathname);

      const pageTitle = getPageTitle();

      changeTitle(pageTitle.headerTitle);

      document.title = pageTitle.documentTitle
        ? `COOPERCITRUS - ${pageTitle.documentTitle}`
        : 'COOPERCITRUS';
    }
  }, [pathName, locationObject.pathname]);

  const displayStyle = (window.innerWidth <= 425 && collapsed) ? 'none' : 'block';
  const userPermissions = getUserPermissions();

  const finalItemsToRender = [
    ...(
      menu.map((item, index) => {
        if (!item.visibility.includes(userType)) {
          return null;
        }

        if ('checkPermissionExpressionMethod' in item && item['checkPermissionExpressionMethod'] != null) {
          if (!item['checkPermissionExpressionMethod']()) {
            return null;
          }
        }

        if ('hasEveryPermission' in item && item.hasEveryPermission != null) {
          if (
            !(item.hasEveryPermission || []).every(
              (element) => (userPermissions || []).includes(element),
            )
          ) {
            return null;
          }
        }

        if (
          !(item.permissions || [])
            .some(
              (element) => (userPermissions || []).includes(element),
            )
        ) {
          return null;
        }

        return (
          item.standalone
            ? renderMenuItem(item)
            : renderSubMenu(item, index)
        );
      })
    ),
  ];

  return (
    <Layout.Sider
      id="SidebarComponent"
      trigger={null}
      style={{ display: displayStyle }}
      collapsible
      collapsed={collapsed}
      width={(window.innerWidth <= 425) ? window.innerWidth * 0.8 : 260}
    >
      <div className={`sidebar-logo-section${collapsed ? '-collapsed' : ''}`}>
        <img src={collapsed ? logoCollapsed : logo} alt="" />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        defaultOpenKeys={getOpenKeys()}
        selectedKeys={[pathName]}
        items={finalItemsToRender}
      />
      <div className="version-container">
        <div className="version">
          {BUILD_NUMBER ? `${VERSION}-${BUILD_NUMBER}` : VERSION}
        </div>
      </div>
    </Layout.Sider>
  );
};

SidebarComponent.propTypes = {};
SidebarComponent.defaultProps = {};

export default SidebarComponent;
